import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const CityLinksSection = ({ initialCities, initialPopularServices }) => {
  const { t, i18n } = useTranslation('home');
  const lang = i18n.language;
  const hasInitialData = !!(initialCities && initialPopularServices);
  const [cities, setCities] = useState(() => (initialCities ? initialCities.slice(0, 4) : []));
  const [popularServices, setPopularServices] = useState(() => initialPopularServices ?? []);
  const [loading, setLoading] = useState(!hasInitialData);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasInitialData) return; // already seeded from /api/home
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only check; hasInitialData is read once, not a re-trigger condition
  }, []);

  const fetchData = async () => {
    try {
      const [cityStatsRes, servicesRes] = await Promise.all([
        fetch(`${import.meta.env.VITE_REACT_APP_API_URL}/api/cities/city-stats`),
        fetch(`${import.meta.env.VITE_REACT_APP_API_URL}/api/cities/popular-services?limit=5`),
      ]);
      if (!cityStatsRes.ok || !servicesRes.ok) throw new Error("Request failed");
      const [cityStatsData, servicesData] = await Promise.all([
        cityStatsRes.json(),
        servicesRes.json(),
      ]);
      if (cityStatsData.success) setCities(cityStatsData.data.slice(0, 4));
      if (servicesData.success) setPopularServices(servicesData.data);
    } catch (err) {
      console.error("Failed to fetch city links data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getServiceName = (service) =>
    (lang === 'en' && service.name_en) || (lang === 'ru' && service.name_ru) || service.name;

  const handleServiceClick = (service) =>
    navigate(`/search?service_id=${encodeURIComponent(service.id)}`);
  const handleCityServiceClick = (city, service) =>
    navigate(`/search?city=${encodeURIComponent(city)}&service_id=${encodeURIComponent(service.id)}`);
  const handleViewAllCity = (city) =>
    navigate(`/search?city=${encodeURIComponent(city)}`);

  if (loading) {
    return (
      <section style={{ padding: "80px 0", background: "#f7f7f8" }}>
        <div style={{ textAlign: "center", color: "#9a9aab", fontSize: "13px", fontWeight: 500 }}>
          {t('city_loading')}
        </div>
      </section>
    );
  }

  return (
    <section style={{
      padding: "90px 0",
      background: "#f7f7f8",
      borderTop: "1px solid #ebebef",
      position: "relative",
    }}>
      <style>{`
        .city-links-wrapper {
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 32px;
        }
        .city-links-card {
          background: #ffffff;
          border: 1px solid #ebebef;
          border-radius: 18px;
          box-shadow: 0 1px 3px rgba(13,22,25,0.04), 0 12px 32px rgba(13,22,25,0.06);
          padding: 40px 36px;
        }
        .city-links-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 32px;
        }
        @media (max-width: 900px) {
          .city-links-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
          }
        }
        @media (max-width: 600px) {
          .city-links-wrapper {
            padding: 0 16px;
          }
          .city-links-card {
            padding: 28px 20px;
          }
          .city-links-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }
        }
        @media (max-width: 380px) {
          .city-links-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
        }
      `}</style>
      <div className="city-links-wrapper">
        <p style={{
          fontSize: "11px", fontWeight: 700, letterSpacing: "0.18em",
          textTransform: "uppercase", color: "#a346d5",
          textAlign: "center", marginBottom: "8px",
        }}>
          {t('city_eyebrow')}
        </p>
        <h2 style={{
          fontSize: "clamp(20px, 3vw, 30px)", fontWeight: 800,
          textAlign: "center", color: "#0f0f14",
          marginBottom: "56px", lineHeight: 1.2, letterSpacing: "-0.02em",
        }}>
          {t('city_heading')}
        </h2>

        <div className="city-links-card">
          <div className="city-links-grid">

            <div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "4px 10px", borderRadius: "99px",
                background: "rgba(163,70,213,0.08)", marginBottom: "20px",
              }}>
                <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#a346d5" }} />
                <span style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.12em", color: "#a346d5", textTransform: "uppercase" }}>
                  {t('city_most_searched')}
                </span>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {popularServices.map((service) => (
                  <li key={service.id} style={{ marginBottom: "10px" }}>
                    <button
                      onClick={() => handleServiceClick(service)}
                      style={{
                        background: "none", border: "none", padding: 0, cursor: "pointer",
                        fontSize: "13px", fontWeight: 600,
                        color: "#5b5b6b", textAlign: "left",
                        transition: "color 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = "#0f0f14"}
                      onMouseLeave={e => e.currentTarget.style.color = "#5b5b6b"}
                    >
                      {getServiceName(service)}
                    </button>
                  </li>
                ))}
                <li style={{ marginTop: "16px" }}>
                  <button
                    onClick={() => navigate("/gallery")}
                    style={{
                      background: "none", border: "none", padding: 0, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: "4px",
                      fontSize: "12px", fontWeight: 700, color: "#a346d5",
                      transition: "color 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = "#0f0f14"}
                    onMouseLeave={e => e.currentTarget.style.color = "#a346d5"}
                  >
                    {t('city_see_gallery')} <ChevronRight style={{ width: 13, height: 13 }} />
                  </button>
                </li>
              </ul>
            </div>

            {cities.map((city) => (
              <div key={city.salon_city}>
                <div style={{ marginBottom: "20px" }}>
                  <span style={{
                    fontSize: "14px", fontWeight: 700, color: "#0f0f14",
                    display: "block",
                  }}>
                    {city.salon_city}
                  </span>
                  <div style={{
                    marginTop: "6px", width: "28px", height: "2px",
                    borderRadius: "99px", background: "#a346d5",
                  }} />
                </div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {popularServices.slice(0, 3).map((service) => (
                    <li key={service.id} style={{ marginBottom: "10px" }}>
                      <button
                        onClick={() => handleCityServiceClick(city.salon_city, service)}
                        style={{
                          background: "none", border: "none", padding: 0, cursor: "pointer",
                          fontSize: "13px", fontWeight: 400,
                          color: "#9a9aab", textAlign: "left",
                          transition: "color 0.15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = "#0f0f14"}
                        onMouseLeave={e => e.currentTarget.style.color = "#9a9aab"}
                      >
                        {t('city_service_in', { service: getServiceName(service), city: city.salon_city })}
                      </button>
                    </li>
                  ))}
                  <li style={{ marginTop: "16px" }}>
                    <button
                      onClick={() => handleViewAllCity(city.salon_city)}
                      style={{
                        background: "none", border: "none", padding: 0, cursor: "pointer",
                        display: "inline-flex", alignItems: "center", gap: "4px",
                        fontSize: "12px", fontWeight: 700, color: "#a346d5",
                        transition: "color 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = "#0f0f14"}
                      onMouseLeave={e => e.currentTarget.style.color = "#a346d5"}
                    >
                      {t('city_see_all', { count: city.salon_count })} <ChevronRight style={{ width: 13, height: 13 }} />
                    </button>
                  </li>
                </ul>
              </div>
            ))}

          </div>
        </div>
      </div>
    </section>
  );
};

export default CityLinksSection;